import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { usePortfolios } from "@/hooks/useData";
import { Sparkline } from "@/components/widgets/StatWidgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { fmtMoney, fmtPct, pnlClass } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Plus, FolderOpen, MoreVertical, Pencil, Trash2, ArrowRight, Layers } from "lucide-react";

function PortfolioDialog({ open, setOpen, editing, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "" });
  const [busy, setBusy] = useState(false);

  // reset the form whenever the dialog is (re)opened
  const [seeded, setSeeded] = useState(null);
  if (open && seeded !== (editing?.portfolio_id || "new")) {
    setSeeded(editing?.portfolio_id || "new");
    setForm({ name: editing?.name || "", description: editing?.description || "" });
  }
  if (!open && seeded !== null) setSeeded(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/portfolios/${editing.portfolio_id}`, form);
        toast.success("Portfolio updated");
      } else {
        await api.post("/portfolios", form);
        toast.success("Portfolio created");
      }
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save portfolio");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Rename portfolio" : "New portfolio"}</DialogTitle>
          <DialogDescription className="text-xs">
            A portfolio groups related accounts, one for your own trading, one per EA, one per client.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="My EAs" data-testid="portfolio-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Description <span className="text-muted-foreground">(optional)</span></Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Three live accounts running Atlas Grid" data-testid="portfolio-description-input" />
          </div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="portfolio-submit-button">
            {editing ? "Save changes" : "Create portfolio"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ target, setTarget, onDone }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/portfolios/${target.portfolio_id}`);
      toast.success(data.moved_accounts
        ? `Portfolio deleted. ${data.moved_accounts} account(s) moved to your first portfolio.`
        : "Portfolio deleted");
      setTarget(null);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete portfolio");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!busy && !o) setTarget(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Delete "{target?.name}"?</DialogTitle>
          <DialogDescription className="text-xs">
            {target?.account_count
              ? `Its ${target.account_count} account(s) will be moved to your first portfolio. No trading data is deleted.`
              : "This portfolio is empty. Nothing else will change."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setTarget(null)} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={remove} disabled={busy} data-testid="portfolio-delete-confirm">
            Delete portfolio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PortfoliosPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: portfolios, isLoading, refetch } = usePortfolios();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const invalidate = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: ["portfolio-accounts"] });
  };

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:py-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">Portfolios</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Group your accounts however you trade. One portfolio for your own account, one per EA, one per client,
              each with its own combined performance.
            </p>
          </div>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-portfolio-button">
            <Plus className="mr-1.5 h-4 w-4" /> New portfolio
          </Button>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading && [...Array(3)].map((_, i) => <Skeleton key={i} className="h-52 rounded-2xl" />)}
          {(portfolios || []).map((p) => (
            <div key={p.portfolio_id}
              className="group cursor-pointer rounded-2xl border bg-card p-4 shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-shadow duration-150 hover:shadow-[0_10px_30px_rgba(2,6,23,0.10)]"
              onClick={() => navigate(`/app/portfolio/${p.portfolio_id}`)}
              data-testid={`portfolio-card-${p.portfolio_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    {p.is_demo ? <Layers className="h-4 w-4 text-primary" /> : <FolderOpen className="h-4 w-4 text-primary" />}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {p.account_count} account{p.account_count === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
                {p.is_demo ? (
                  <Badge variant="secondary" className="shrink-0 text-[10px]">Sample</Badge>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={(e) => e.stopPropagation()} data-testid={`portfolio-menu-${p.portfolio_id}`}>
                        <MoreVertical className="h-3.5 w-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => { setEditing(p); setDialogOpen(true); }}
                        data-testid={`portfolio-rename-${p.portfolio_id}`}>
                        <Pencil className="mr-2 h-3.5 w-3.5" /> Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive" onClick={() => setDeleting(p)}
                        data-testid={`portfolio-delete-${p.portfolio_id}`}>
                        <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {p.account_count === 0 ? (
                <p className="mt-4 rounded-xl bg-surface-2 p-3 text-xs leading-relaxed text-muted-foreground">
                  Empty portfolio. Open it to connect your first MT4/MT5 account.
                </p>
              ) : (
                <>
                  <div className="mt-4 flex items-end justify-between">
                    <div>
                      <div className="font-display text-xl font-semibold tabular-nums" data-testid={`portfolio-balance-${p.portfolio_id}`}>
                        {fmtMoney(p.balance, p.currency)}
                      </div>
                      <div className={cn("text-xs font-medium tabular-nums", pnlClass(p.total_gain_pct))}>
                        {fmtPct(p.total_gain_pct, true)} all-time · {fmtMoney(p.total_pl, p.currency)}
                      </div>
                    </div>
                    <Sparkline points={p.sparkline} width={110} height={34}
                      className={p.total_gain_pct >= 0 ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]"} />
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 border-t pt-3 text-center">
                    <div>
                      <div className="text-xs font-semibold tabular-nums">{p.win_rate}%</div>
                      <div className="text-[10px] text-muted-foreground">Win rate</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold tabular-nums text-[hsl(var(--loss))]">-{fmtPct(p.max_drawdown)}</div>
                      <div className="text-[10px] text-muted-foreground">Max DD</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold tabular-nums">{p.total_trades}</div>
                      <div className="text-[10px] text-muted-foreground">Trades</div>
                    </div>
                  </div>
                  {p.mixed_currency && (
                    <p className="mt-2 text-[10px] text-muted-foreground">
                      Mixed account currencies, totals are indicative.
                    </p>
                  )}
                </>
              )}
              <div className="mt-3 flex items-center gap-1 text-xs font-medium text-primary">
                Open portfolio <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-0.5" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <PortfolioDialog open={dialogOpen} setOpen={setDialogOpen} editing={editing} onSaved={invalidate} />
      <DeleteDialog target={deleting} setTarget={setDeleting} onDone={invalidate} />
    </AppShell>
  );
}
